import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Container,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Stack,
  Button,
  TableSortLabel,
} from '@mui/material';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Transaction } from '../types';
import { useTransactions, useAccountBalances, useOrganizationSettings, useAvailableFiscalYears } from '../hooks/useTransactions';
import { getCurrentFiscalYear, getFiscalYearStartDate, getFiscalYearEndDate } from '../utils/fiscalYear';

// Chart.js の設定
ChartJS.register(ArcElement, ChartTooltip, Legend);


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const FinancialDetails: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  
  // 検索・フィルタリング用state
  const [filterType, setFilterType] = useState<'income' | 'expense' | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // ソート用state
  type SortField = 'transaction_date' | 'category' | 'description' | 'amount';
  const [sortField, setSortField] = useState<SortField>('transaction_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // 組織設定と利用可能な年度を取得
  const { data: orgSettings, isLoading: orgSettingsLoading } = useOrganizationSettings();
  const { data: availableFiscalYears, isLoading: availableYearsLoading } = useAvailableFiscalYears();
  
  // 年度選択用state - 組織設定が読み込まれてから初期化
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<number | null>(null);
  
  // 組織設定が読み込まれたら会計年度を設定
  React.useEffect(() => {
    if (orgSettings?.fiscalYearStartMonth && selectedFiscalYear === null) {
      setSelectedFiscalYear(getCurrentFiscalYear(orgSettings.fiscalYearStartMonth));
    }
  }, [orgSettings?.fiscalYearStartMonth, selectedFiscalYear]);
  
  // 利用可能な年度リストを取得（実際のデータが存在する範囲）
  const getAvailableYears = () => {
    return availableFiscalYears || [];
  };
  
  // 選択された年度の日付範囲を計算
  const fiscalYearStartDate = selectedFiscalYear && orgSettings?.fiscalYearStartMonth 
    ? getFiscalYearStartDate(selectedFiscalYear, orgSettings.fiscalYearStartMonth)
    : null;
  const fiscalYearEndDate = selectedFiscalYear && orgSettings?.fiscalYearStartMonth
    ? getFiscalYearEndDate(selectedFiscalYear, orgSettings.fiscalYearStartMonth) 
    : null;
  
  // 実データフェッチ（会計年度ベース）
  const { data: transactions, isLoading, error } = useTransactions(
    fiscalYearStartDate && fiscalYearEndDate ? {
      startDate: fiscalYearStartDate.toISOString().split('T')[0],
      endDate: fiscalYearEndDate.toISOString().split('T')[0]
    } : undefined
  );
  const { data: accountBalances, isLoading: balancesLoading, error: balancesError } = useAccountBalances();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(amount);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // タブ切り替え時にページを1に戻す
    setCurrentPage(1);
  };

  // フィルターリセット機能
  const handleResetFilters = () => {
    setFilterType('all');
    setFilterCategory('');
    setCurrentPage(1);
  };
  
  // ソート機能
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // ソート変更時はページを1に戻す
  };
  
  // データをソートする関数
  const sortTransactions = (transactions: Transaction[]): Transaction[] => {
    return [...transactions].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortField) {
        case 'transaction_date':
          aValue = new Date(a.transaction_date).getTime();
          bValue = new Date(b.transaction_date).getTime();
          break;
        case 'category':
          aValue = a.category || '';
          bValue = b.category || '';
          break;
        case 'description':
          aValue = a.description || '';
          bValue = b.description || '';
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // 年度変更ハンドラー
  const handleFiscalYearChange = (event: any) => {
    setSelectedFiscalYear(Number(event.target.value));
    setCurrentPage(1); // ページを1に戻す
  };

  // ローディング・エラーハンドリング
  if (isLoading || balancesLoading || orgSettingsLoading || availableYearsLoading || selectedFiscalYear === null) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || balancesError) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          データの取得中にエラーが発生しました: {error?.message || balancesError?.message}
        </Alert>
      </Container>
    );
  }

  // トランザクションを直接使用（型変換なし）
  const allTransactions = transactions || [];
  
  // フィルタリング機能
  const getFilteredTransactions = (accountType: 'general' | 'reserve') => {
    let filtered = allTransactions.filter(t => t.account_type === accountType);
    
    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.type === filterType);
    }
    
    if (filterCategory.trim()) {
      filtered = filtered.filter(t => 
        t.category?.toLowerCase().includes(filterCategory.toLowerCase()) ||
        t.description?.toLowerCase().includes(filterCategory.toLowerCase())
      );
    }
    
    return filtered;
  };
  
  const generalTransactions = sortTransactions(getFilteredTransactions('general'));
  const reserveTransactions = sortTransactions(getFilteredTransactions('reserve'));
  
  // ページネーション用
  const getCurrentPageData = (data: Transaction[]) => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  };
  
  const generalPageData = getCurrentPageData(generalTransactions);
  const reservePageData = getCurrentPageData(reserveTransactions);
  const generalTotalPages = Math.ceil(generalTransactions.length / pageSize);
  const reserveTotalPages = Math.ceil(reserveTransactions.length / pageSize);

  const getTransactionColor = (type: 'income' | 'expense') => {
    return type === 'income' ? 'success' : 'error';
  };

  // カテゴリ別支出分析データの作成
  const createCategoryExpenseChart = (transactions: Transaction[], accountType: 'general' | 'reserve') => {
    const expenseTransactions = transactions.filter(t => 
      t.account_type === accountType && t.type === 'expense'
    );
    
    const categoryExpense: Record<string, number> = {};
    
    expenseTransactions.forEach(transaction => {
      const category = transaction.category || 'その他';
      categoryExpense[category] = (categoryExpense[category] || 0) + transaction.amount;
    });
    
    const labels = Object.keys(categoryExpense);
    const data = Object.values(categoryExpense);
    
    // カテゴリ別の色を生成
    const backgroundColors = [
      '#FF6384',
      '#36A2EB', 
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40',
      '#FF6384',
      '#C9CBCF',
      '#4BC0C0',
      '#FF6384'
    ];

    return {
      labels,
      datasets: [
        {
          label: '支出額',
          data,
          backgroundColor: backgroundColors.slice(0, labels.length),
          borderColor: backgroundColors.slice(0, labels.length).map(color => color.replace('0.8', '1')),
          borderWidth: 1,
        },
      ],
    };
  };

  // カテゴリ別収入分析データの作成
  const createCategoryIncomeChart = (transactions: Transaction[], accountType: 'general' | 'reserve') => {
    const incomeTransactions = transactions.filter(t => 
      t.account_type === accountType && t.type === 'income'
    );
    
    const categoryIncome: Record<string, number> = {};
    
    incomeTransactions.forEach(transaction => {
      const category = transaction.category || 'その他';
      categoryIncome[category] = (categoryIncome[category] || 0) + transaction.amount;
    });
    
    const labels = Object.keys(categoryIncome);
    const data = Object.values(categoryIncome);
    
    // カテゴリ別の色を生成（緑系）
    const backgroundColors = [
      '#4CAF50',
      '#8BC34A', 
      '#CDDC39',
      '#00BCD4',
      '#009688',
      '#2196F3',
      '#3F51B5',
      '#673AB7',
      '#9C27B0',
      '#E91E63'
    ];

    return {
      labels,
      datasets: [
        {
          label: '収入額',
          data,
          backgroundColor: backgroundColors.slice(0, labels.length),
          borderColor: backgroundColors.slice(0, labels.length).map(color => color.replace('0.8', '1')),
          borderWidth: 1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          boxWidth: 12,
          padding: 15,
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${formatCurrency(value)} (${percentage}%)`;
          },
        },
      },
    },
  };

  // 月別サマリー計算 - 選択された会計年度の最新月を使用
  const calculateMonthlySummary = (transactions: Transaction[], accountType: 'general' | 'reserve') => {
    if (!transactions || transactions.length === 0) {
      return { income: 0, expense: 0, net: 0, budgetRate: '0.0' };
    }

    // 選択された会計年度内の最新の取引日を特定
    const accountTransactions = transactions.filter(t => t.account_type === accountType);
    if (accountTransactions.length === 0) {
      return { income: 0, expense: 0, net: 0, budgetRate: '0.0' };
    }

    // 最新の取引日の年月を取得
    const latestTransaction = accountTransactions.reduce((latest, current) => {
      return new Date(current.transaction_date) > new Date(latest.transaction_date) ? current : latest;
    });
    
    const latestDate = new Date(latestTransaction.transaction_date);
    const latestYear = latestDate.getFullYear();
    const latestMonth = latestDate.getMonth() + 1;

    // その年月の取引のみを抽出
    const currentMonthTransactions = accountTransactions.filter(t => {
      const transactionDate = new Date(t.transaction_date);
      const transactionYear = transactionDate.getFullYear();
      const transactionMonth = transactionDate.getMonth() + 1;
      return transactionYear === latestYear && transactionMonth === latestMonth;
    });

    const income = currentMonthTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const expense = currentMonthTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);

    const net = income - expense;
    const budgetRate = income > 0 ? ((expense / income) * 100).toFixed(1) : '0.0';

    return { income, expense, net, budgetRate };
  };

  const generalMonthlySummary = calculateMonthlySummary(allTransactions, 'general');
  // const reserveMonthlySummary = calculateMonthlySummary(allTransactions, 'reserve');  // 現在未使用

  // 積立会計データ計算
  const reserveBalance = accountBalances?.find(balance => balance.account_type === 'reserve')?.balance || 0;
  const currentFiscalYearReserveIncome = allTransactions
    .filter(t => t.account_type === 'reserve' && t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          収支詳細
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>年度</InputLabel>
          <Select
            value={selectedFiscalYear}
            onChange={handleFiscalYearChange}
            label="年度"
          >
            {getAvailableYears().map(year => (
              <MenuItem key={year} value={year}>
                {year}年度
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="一般会計" />
        <Tab label="積立会計" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        {/* 検索・フィルタリング */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            検索・フィルタリング
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>取引種別</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value as 'income' | 'expense' | 'all');
                  setCurrentPage(1);
                }}
                label="取引種別"
              >
                <MenuItem value="all">全て</MenuItem>
                <MenuItem value="income">収入</MenuItem>
                <MenuItem value="expense">支出</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              size="small"
              label="カテゴリ・摘要検索"
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="例：管理費、修繕費"
              sx={{ minWidth: 200 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>表示件数</InputLabel>
              <Select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                label="表示件数"
              >
                <MenuItem value={10}>10件</MenuItem>
                <MenuItem value={20}>20件</MenuItem>
                <MenuItem value={50}>50件</MenuItem>
                <MenuItem value={100}>100件</MenuItem>
              </Select>
            </FormControl>
            
            <Button 
              variant="outlined" 
              onClick={handleResetFilters}
              size="small"
            >
              リセット
            </Button>
          </Box>
          
          {/* 検索結果サマリー */}
          <Typography variant="body2" color="text.secondary">
            一般会計: {generalTransactions.length}件の取引が見つかりました
          </Typography>
        </Paper>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* 一般会計取引履歴 */}
          <Box sx={{ flex: '2 1 500px', minWidth: '500px' }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                一般会計 取引履歴
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'transaction_date'}
                          direction={sortField === 'transaction_date' ? sortDirection : 'asc'}
                          onClick={() => handleSort('transaction_date')}
                        >
                          日付
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'category'}
                          direction={sortField === 'category' ? sortDirection : 'asc'}
                          onClick={() => handleSort('category')}
                        >
                          カテゴリ
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'description'}
                          direction={sortField === 'description' ? sortDirection : 'asc'}
                          onClick={() => handleSort('description')}
                        >
                          摘要
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>メモ</TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={sortField === 'amount'}
                          direction={sortField === 'amount' ? sortDirection : 'asc'}
                          onClick={() => handleSort('amount')}
                        >
                          金額
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {generalPageData.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{transaction.transaction_date}</TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.category} 
                            size="small"
                            color={getTransactionColor(transaction.type)} 
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={transaction.description || ''}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {transaction.description || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={transaction.reference_number || ''}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                              {transaction.reference_number || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color={getTransactionColor(transaction.type)}
                            fontWeight={transaction.type === 'income' ? 'bold' : 'normal'}
                          >
                            {formatCurrency(transaction.amount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* ページネーション */}
              {generalTotalPages > 1 && (
                <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <Pagination 
                    count={generalTotalPages}
                    page={currentPage}
                    onChange={(event, value) => setCurrentPage(value)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              )}
            </Paper>
          </Box>

          {/* カテゴリ別支出分析 */}
          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                カテゴリ別支出分析
              </Typography>
              <Box sx={{ height: 300, mb: 2 }}>
                {generalTransactions.filter(t => t.type === 'expense').length > 0 ? (
                  <Pie 
                    data={createCategoryExpenseChart(allTransactions, 'general')} 
                    options={chartOptions}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">
                      支出データがありません
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* 月別推移 */}
        <Box sx={{ mt: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              一般会計 月別サマリー
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="success.main">
                      {formatCurrency(generalMonthlySummary.income)}
                    </Typography>
                    <Typography variant="body2">今月の収入</Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="error.main">
                      {formatCurrency(generalMonthlySummary.expense)}
                    </Typography>
                    <Typography variant="body2">今月の支出</Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color={generalMonthlySummary.net >= 0 ? "success.main" : "error.main"}>
                      {formatCurrency(generalMonthlySummary.net)}
                    </Typography>
                    <Typography variant="body2">今月の収支</Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">
                      {generalMonthlySummary.budgetRate}%
                    </Typography>
                    <Typography variant="body2">支出率</Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Paper>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* 検索・フィルタリング */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            検索・フィルタリング
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>取引種別</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value as 'income' | 'expense' | 'all');
                  setCurrentPage(1);
                }}
                label="取引種別"
              >
                <MenuItem value="all">全て</MenuItem>
                <MenuItem value="income">収入</MenuItem>
                <MenuItem value="expense">支出</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              size="small"
              label="カテゴリ・摘要検索"
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="例：積立金、修繕費"
              sx={{ minWidth: 200 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>表示件数</InputLabel>
              <Select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                label="表示件数"
              >
                <MenuItem value={10}>10件</MenuItem>
                <MenuItem value={20}>20件</MenuItem>
                <MenuItem value={50}>50件</MenuItem>
                <MenuItem value={100}>100件</MenuItem>
              </Select>
            </FormControl>
            
            <Button 
              variant="outlined" 
              onClick={handleResetFilters}
              size="small"
            >
              リセット
            </Button>
          </Box>
          
          {/* 検索結果サマリー */}
          <Typography variant="body2" color="text.secondary">
            積立会計: {reserveTransactions.length}件の取引が見つかりました
          </Typography>
        </Paper>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* 積立会計取引履歴 */}
          <Box sx={{ flex: '2 1 500px', minWidth: '500px' }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                積立会計 取引履歴
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'transaction_date'}
                          direction={sortField === 'transaction_date' ? sortDirection : 'asc'}
                          onClick={() => handleSort('transaction_date')}
                        >
                          日付
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'category'}
                          direction={sortField === 'category' ? sortDirection : 'asc'}
                          onClick={() => handleSort('category')}
                        >
                          カテゴリ
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'description'}
                          direction={sortField === 'description' ? sortDirection : 'asc'}
                          onClick={() => handleSort('description')}
                        >
                          摘要
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>メモ</TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={sortField === 'amount'}
                          direction={sortField === 'amount' ? sortDirection : 'asc'}
                          onClick={() => handleSort('amount')}
                        >
                          金額
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reservePageData.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{transaction.transaction_date}</TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.category} 
                            size="small"
                            color={getTransactionColor(transaction.type)} 
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={transaction.description || ''}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {transaction.description || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={transaction.reference_number || ''}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                              {transaction.reference_number || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color={getTransactionColor(transaction.type)}
                            fontWeight={transaction.type === 'income' ? 'bold' : 'normal'}
                          >
                            {formatCurrency(transaction.amount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* ページネーション */}
              {reserveTotalPages > 1 && (
                <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <Pagination 
                    count={reserveTotalPages}
                    page={currentPage}
                    onChange={(event, value) => setCurrentPage(value)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              )}
            </Paper>
          </Box>

          {/* カテゴリ別収支分析 */}
          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* 積立状況 */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  積立状況
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">現在残高</Typography>
                  <Typography variant="h5" color="primary">
                    {formatCurrency(reserveBalance)}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">今年度積立額</Typography>
                  <Typography variant="h6" color="success.main">
                    {formatCurrency(currentFiscalYearReserveIncome)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2">次回大規模修繕予定額</Typography>
                  <Typography variant="h6" color="warning.main">
                    {formatCurrency(12000000)}
                  </Typography>
                </Box>
              </Paper>

              {/* カテゴリ別収入分析 */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  カテゴリ別収入分析
                </Typography>
                <Box sx={{ height: 250 }}>
                  {reserveTransactions.filter(t => t.type === 'income').length > 0 ? (
                    <Pie 
                      data={createCategoryIncomeChart(allTransactions, 'reserve')} 
                      options={chartOptions}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography variant="body2" color="text.secondary">
                        収入データがありません
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </TabPanel>
    </Container>
  );
};

export default FinancialDetails;